import { useCallback, useMemo } from 'react'
import { useLatest } from 'react-use'
import {
  BEAM_WITH_WALL_LINEWIDTH,
  elementTypeToLinewidthFloorplan,
  TargetPlane,
  WALL_WITH_BEAM_LINEWIDTH,
} from '@scene'
import { reject } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import {
  useEmitFloorplanClickListener,
  useFloorplanGuidToHidden,
  useGuidToCrossSectionDimensions,
  useGuidToElementIncludingRips,
  useHiddenElements,
  useMouseMovedSinceMouseDown,
  useStoreyToElementTypeAndZTranslation,
  useStoreyToElementTypeToOpacity,
  useFloorplanModel,
  useIsPlanView,
} from '@editor/hooks'
import useIsStiffeningPlanPreview from '@editor/pages/PlanView/hooks/useIsStiffeningPlanPreview'
import usePlanViewTab from '@editor/pages/PlanView/hooks/usePlanViewTab'
import usePositionPlanGeometryGuidToHidden from '@editor/pages/PlanView/hooks/usePositionPlanGeometryGuidToHidden'
import { getPlanViewStorey } from '@editor/pages/PlanView/utils'
import { FloorplanModelLabels } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components'
import {
  useEnhanceClickEvent,
  useWallsWithBeamOverlap,
} from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks'
import { ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY } from '@editor/pages/StructuralPlanning/components/VerticalTransmitterList/constants'
import { useFilterState } from '@editor/pages/StructuralPlanning/hooks/useFilterState'
import {
  useControlStore,
  useEditElementStore,
  useFloorplanStore,
  useModelStore,
  useSceneStore,
  roofStoreyKey,
} from '@editor/stores'
import { isInPlanStorey, isPlanStoreyElement } from '@editor/utils'
import { FloorplanMesh, ImmutableVector3, sceneConfig } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { coordinateEventName } from '@ui/events'
import { useTriggerContextMenu } from '@ui/hooks'
import RealThicknessWallsMesh from './components/RealThicknessWallsMesh'
import StiffeningSegmentsFootprintMesh from './components/StiffeningSegmentsFootprintMesh'
import {
  isPlanColoringMode,
  PLAN_COLORING_OPACITY,
  PLAN_FOOTPRINT_FILL,
  PLAN_ROOF_POSITION_OUTLINE,
  PLAN_WALL_COLORS,
} from './planColoring'

const FloorplanScene = () => {
  const storey = useControlStore(state => state.floorplanState.storey)
  const isPlanView = useIsPlanView()
  const isStiffeningPlanPreview = useIsStiffeningPlanPreview()

  const floorplanModel = useFloorplanModel()
  const { visibleStoreys, availableStoreys, model } = useModelStore(state => state)

  const planStorey = isPlanView ? getPlanViewStorey(storey, visibleStoreys) : null

  const {
    showBeams,
    showVerticalSlabs,
    showRoofSlabs,
    showPurlins,
    showVerticalRoofSlabs,
    showSlabs,
    showColumns,
    showRips,
  } = useFilterState()

  const guidToColor = useSceneStore(state => state.guidToColor)
  const coloringMode = useControlStore(state => state.coloringMode)
  const planColoringMode = isPlanColoringMode(coloringMode) ? coloringMode : null
  const storeyToWallLinewidth = useSceneStore(state => state.storeyToWallLinewidth)
  const showRealWallThickness = useModelStore(state => state.showRealWallThickness)

  const hiddenStoreys = useMemo(() => {
    const visible = new Set(visibleStoreys)
    // Dach is previewed on the top storey's floorplan. Keep that level visible so
    // ceiling positions assigned to the roof (UserDefined) can be drawn there.
    if (isPlanView && storey) visible.add(storey)
    return reject([...availableStoreys], availableStorey => visible.has(availableStorey))
  }, [availableStoreys, isPlanView, storey, visibleStoreys])

  const { openContextMenu } = useTriggerContextMenu()

  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const hiddenElements = useHiddenElements()
  const elementTypeToSelectableGroups = useFloorplanStore(
    state => state.elementTypeToSelectableGroups,
  )
  const elementSelectionDisabled = useControlStore(state => state.elementSelectionDisabled)

  const handleContextMenuElement = useCallback(
    (element: string, event: ThreeEvent<MouseEvent>) => {
      // The plan view offers no element actions (hide/properties) — it only previews the plan.
      if (isPlanView) return

      if (hiddenElements.has(element)) return

      if (mouseMoved.current) return

      event.stopPropagation()
      openContextMenu(
        event.point,
        element,
        {
          hide: {
            guid: element,
          },
          properties: {
            guid: element,
          },
        },
        {},
      )
    },
    [isPlanView, hiddenElements, mouseMoved, openContextMenu],
  )

  const guidToHidden = useFloorplanGuidToHidden()
  // The position plan preview leaves out the geometry the PDF leaves out, but keeps the labels.
  const positionPlanGeometryGuidToHidden = usePositionPlanGeometryGuidToHidden()
  const meshGuidToHidden = useMemo(
    () => ({ ...guidToHidden, ...positionPlanGeometryGuidToHidden }),
    [guidToHidden, positionPlanGeometryGuidToHidden],
  )

  const elementTypeToSelectable = useMemo<Record<ElementTypes, boolean>>(() => {
    const allElementTypes: ElementTypes[] = [
      'outer_walls',
      'inner_walls',
      'slabs',
      'roof_slabs',
      'vertical_slabs',
      'vertical_roof_slabs',
      'beams',
      'columns',
      'purlins',
      'rips',
      'lintels',
      'slab_beams',
      'roof_slab_beams',
      'standard_rips',
      'foundation',
    ]

    // While element selection is disabled (e.g. "Import auswählen" mode on the IFC import page),
    // make all floorplan elements non-selectable so only the IFC GLB model reacts to the cursor.
    const defaultSelectable = !elementSelectionDisabled

    const merged: Record<ElementTypes, boolean> = allElementTypes.reduce((acc, type) => {
      acc[type] = defaultSelectable
      return acc
    }, {} as Record<ElementTypes, boolean>)

    if (!defaultSelectable) return merged

    Object.values(elementTypeToSelectableGroups).forEach(group => {
      Object.entries(group).forEach(([type, selectable]) => {
        if (selectable === false) {
          merged[type as ElementTypes] = false
        }
      })
    })

    Object.values(elementTypeToSelectableGroups).forEach(group => {
      Object.entries(group).forEach(([type, selectable]) => {
        if (selectable === true && merged[type as ElementTypes] !== false) {
          merged[type as ElementTypes] = true
        }
      })
    })

    return merged
  }, [elementTypeToSelectableGroups, elementSelectionDisabled])

  const storeyToElementTypeAndZTranslation = useStoreyToElementTypeAndZTranslation(storey)

  const storeyToElementTypeToOpacityBase = useStoreyToElementTypeToOpacity(storey)
  const storeyToElementTypeToOpacity = useMemo(() => {
    if (!planColoringMode || !storeyToElementTypeToOpacityBase)
      return storeyToElementTypeToOpacityBase
    // The base only covers the visible storeys (keyed by storey id), but the mesh reads the roof
    // level under its own key (`sceneConfig.RoofStoreyKey`, not `roofStoreyKey`) and the plan view
    // moves ceiling positions onto the floorplan storey, which can be hidden ("Dach"). Without an
    // entry those levels fall back to the mesh's default fill instead of the export's outline.
    const storeys = new Set([
      ...Object.keys(storeyToElementTypeToOpacityBase),
      sceneConfig.RoofStoreyKey,
    ])
    if (storey) storeys.add(storey)
    // The base opacities are the same on every storey.
    const template = Object.values(storeyToElementTypeToOpacityBase)[0]
    return Object.fromEntries(
      [...storeys].map(s => [
        s,
        { ...(storeyToElementTypeToOpacityBase[s] ?? template), ...PLAN_COLORING_OPACITY },
      ]),
    ) as typeof storeyToElementTypeToOpacityBase
  }, [planColoringMode, storey, storeyToElementTypeToOpacityBase])

  // The plan coloring modes recolor only the floorplan meshes: the labels keep reading the scene
  // coloring (empty in these modes), so their colors stay the export's defaults.
  const meshGuidToColor = useMemo(() => {
    if (!planColoringMode) return guidToColor
    const planColors: Record<string, string> = {}
    floorplanModel.columns.forEach(column => (planColors[column.guid] = PLAN_FOOTPRINT_FILL))
    floorplanModel.rips.forEach(rip => (planColors[rip.position_guid] = PLAN_FOOTPRINT_FILL))
    floorplanModel.vertical_roof_slabs.forEach(
      roofSlab => (planColors[roofSlab.guid] = PLAN_ROOF_POSITION_OUTLINE),
    )
    return { ...planColors, ...guidToColor }
  }, [planColoringMode, guidToColor, floorplanModel])

  const guidToCrossSectionDimensions = useGuidToCrossSectionDimensions()

  // artificial id used for storey
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const storeyId = useMemo(() => uuid(), [storey])

  const storeyPoints = useMemo(() => {
    if (!storey) return null

    const upperBoundary = model.storey_boundaries[storey][1]
    return [
      new ImmutableVector3(-100, -100, upperBoundary),
      new ImmutableVector3(-100, 100, upperBoundary),
      new ImmutableVector3(100, 100, upperBoundary),
      new ImmutableVector3(100, -100, upperBoundary),
    ]
  }, [model.storey_boundaries, storey])

  // Clicking

  const emitFloorplanClickListener = useEmitFloorplanClickListener()

  const onClickElementRef = useLatest(emitFloorplanClickListener)

  const { onClickEnhancedRef } = useEnhanceClickEvent(onClickElementRef.current)

  const selectionModeKey = useControlStore(state => state.selectionModeKey)

  // Check if coordinate input mode is active
  const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)

  const guidToElement = useGuidToElementIncludingRips()

  // Shared storey gate for clicking and hovering: elements on a merely-visible
  // storey must neither be selectable nor show the hover highlight.
  const guidToHoverable = useMemo(() => {
    const isAddVerticalTransmissionTargetMode =
      selectionModeKey === ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY

    const hoverable: Record<string, boolean> = {}

    Object.entries(guidToElement).forEach(([guid, element]) => {
      const elementStorey = element.storey

      const isRoofElementOnFloorplan =
        elementStorey === roofStoreyKey &&
        'element_type' in element &&
        (element.element_type === 'purlins' ||
          element.element_type === 'vertical_roof_slabs' ||
          element.element_type === 'roof_slabs')

      const isOnPlanStorey =
        planStorey != null && isPlanStoreyElement(element) && isInPlanStorey(element, planStorey)

      // The plan view only selects the elements drawn on its plan storeys, to show their settings.
      hoverable[guid] = isPlanView
        ? isOnPlanStorey
        : elementStorey === storey ||
          isRoofElementOnFloorplan ||
          isOnPlanStorey ||
          (isAddVerticalTransmissionTargetMode &&
            elementStorey !== undefined &&
            visibleStoreys.has(elementStorey))
    })

    return hoverable
  }, [guidToElement, storey, selectionModeKey, visibleStoreys, planStorey, isPlanView])

  const guidToHoverableRef = useLatest(guidToHoverable)

  const onClickElementWithEnhancement = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      // Don't handle element clicks when coordinate input mode is active
      if (isCoordinateInputMode) return

      if (guidToHoverableRef.current[guid]) {
        event.stopPropagation()
        onClickEnhancedRef.current(guid, event)
      }
    },
    [onClickEnhancedRef, isCoordinateInputMode, guidToHoverableRef],
  )

  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)

  const activeElement = useEditElementStore(state => state.activeElement)

  // The plan view is a preview of the exported plan: it highlights only the element whose
  // settings its "Bauteil" tab shows, not a selection carried over from another page (which is
  // kept and shows again on the other pages).
  // Not while a selected table, comment or image card's form covers the tab.
  const { tab: planViewTab, planElementDrawerType } = usePlanViewTab()
  const isPlanViewElementTab = planViewTab === 'element' && !planElementDrawerType
  const selectedElements = useMemo(() => {
    if (isPlanView) return isPlanViewElementTab && activeElement ? [activeElement] : []
    return [...(multiActiveElements || []), activeElement || '']
  }, [isPlanView, isPlanViewElementTab, multiActiveElements, activeElement])

  const { wallGuids: wallGuidsWithBeamOverlap, beamGuids: beamGuidsWithWallOverlap } =
    useWallsWithBeamOverlap(floorplanModel.walls, floorplanModel.beams)

  const guidToLineWidth = useMemo(() => {
    if (wallGuidsWithBeamOverlap.size === 0 && beamGuidsWithWallOverlap.size === 0) {
      return undefined
    }

    const lineWidthByGuid: Record<string, number> = {}

    wallGuidsWithBeamOverlap.forEach(wallGuid => {
      lineWidthByGuid[wallGuid] = WALL_WITH_BEAM_LINEWIDTH
    })

    beamGuidsWithWallOverlap.forEach(beamGuid => {
      lineWidthByGuid[beamGuid] = BEAM_WITH_WALL_LINEWIDTH
    })

    return lineWidthByGuid
  }, [wallGuidsWithBeamOverlap, beamGuidsWithWallOverlap])

  // Same storeys as the FloorplanMesh draws: in the plan view that includes the previewed
  // floorplan storey, where walls shown on another plan storey are placed.
  const realThicknessWalls = useMemo(() => {
    if (!showRealWallThickness) return []
    const hidden = new Set(hiddenStoreys)
    return floorplanModel.walls.filter(wall => !hidden.has(wall.storey))
  }, [showRealWallThickness, floorplanModel.walls, hiddenStoreys])

  return (
    <group>
      <FloorplanMesh
        modelData={floorplanModel}
        hiddenStoreys={hiddenStoreys}
        guidToColor={meshGuidToColor}
        onClickElement={onClickElementWithEnhancement}
        selectedElements={selectedElements}
        guidToHidden={meshGuidToHidden}
        onContextMenuElement={handleContextMenuElement}
        storeyToElementTypeAndOpacity={storeyToElementTypeToOpacity}
        storeyToElementTypeAndZTranslation={storeyToElementTypeAndZTranslation}
        guidToCrossSection={guidToCrossSectionDimensions}
        showBeams={showBeams}
        showPurlins={showPurlins}
        showVerticalSlabs={showVerticalSlabs}
        showSlabs={showSlabs}
        showRoofSlabs={showRoofSlabs}
        showColumns={showColumns}
        showRips={showRips}
        showVerticalRoofSlabs={showVerticalRoofSlabs}
        showInnerWalls={!showRealWallThickness}
        showOuterWalls={!showRealWallThickness}
        typeToSelectable={elementTypeToSelectable}
        elementTypeToLinewidth={elementTypeToLinewidthFloorplan}
        storeyToWallLinewidth={storeyToWallLinewidth}
        guidToLineWidth={guidToLineWidth}
        guidToHoverable={guidToHoverable}
      />
      {showRealWallThickness && (
        <RealThicknessWallsMesh
          walls={realThicknessWalls}
          guidToHidden={guidToHidden}
          storeyToElementTypeAndZTranslation={storeyToElementTypeAndZTranslation}
          selectedElements={selectedElements}
          typeToSelectable={elementTypeToSelectable}
          onClickElement={onClickElementWithEnhancement}
          onContextMenuElement={handleContextMenuElement}
          planColors={planColoringMode && PLAN_WALL_COLORS[planColoringMode]}
          hideOpenings={isStiffeningPlanPreview}
        />
      )}
      {showRealWallThickness && isStiffeningPlanPreview && (
        <StiffeningSegmentsFootprintMesh
          walls={realThicknessWalls}
          guidToHidden={guidToHidden}
          storeyToElementTypeAndZTranslation={storeyToElementTypeAndZTranslation}
          typeToSelectable={elementTypeToSelectable}
          onClickElement={onClickElementWithEnhancement}
          onContextMenuElement={handleContextMenuElement}
        />
      )}
      <FloorplanModelLabels
        visibleModel={floorplanModel}
        guidToColor={guidToColor}
        guidToHidden={guidToHidden}
        hideLoadIcons={isPlanView}
      />

      {storeyPoints && <TargetPlane name={storeyId} points={storeyPoints} />}
    </group>
  )
}

export default FloorplanScene
